import React, { useState } from "react";

type Size = 4 | 9;

const generateSolvedBoard = (size: Size): number[][] => {
  const base = size === 4 ? 2 : 3;

  const pattern = (r: number, c: number) =>
    (base * (r % base) + Math.floor(r / base) + c) % size;

  const shuffle = (arr: number[]) =>
    arr.sort(() => Math.random() - 0.5);

  const rows = shuffle([...Array(size).keys()]);
  const cols = shuffle([...Array(size).keys()]);
  const nums = shuffle([...Array(size).keys()].map((n) => n + 1));

  return rows.map((r) =>
    cols.map((c) => nums[pattern(r, c)])
  );
};

const removeCells = (board: number[][], size: Size) => {
  const difficulty = size === 4 ? 10 : 66;
  const newBoard = board.map((row) => [...row]);

  for (let i = 0; i < difficulty; i++) {
    const r = Math.floor(Math.random() * size);
    const c = Math.floor(Math.random() * size);
    newBoard[r][c] = 0;
  }

  return newBoard;
};

const FocusGrid = () => {
  const [size, setSize] = useState<Size>(4);
  const [solution, setSolution] = useState(
    generateSolvedBoard(4)
  );
  const [grid, setGrid] = useState(
    removeCells(solution, 4)
  );

  const regenerate = (newSize: Size) => {
    const solved = generateSolvedBoard(newSize);
    setSolution(solved);
    setGrid(removeCells(solved, newSize));
    setSize(newSize);
  };

  const handleChange = (value: string, r: number, c: number) => {
    if (!/^\d?$/.test(value)) return;

    const num = parseInt(value);
    if (num && (num < 1 || num > size)) return;

    const newGrid = grid.map((row) => [...row]);
    newGrid[r][c] = num || 0;
    setGrid(newGrid);
  };

  const isIncorrect = (r: number, c: number) =>
    grid[r][c] !== 0 && grid[r][c] !== solution[r][c];

  const completed =
    grid.every((row, r) =>
      row.every((cell, c) => cell === solution[r][c])
    );

  return (
    <div className="flex flex-col items-center">

      {/* Size Selector */}
      <div className="flex gap-4 mb-6">
        {[4, 9].map((s) => (
          <button
            key={s}
            onClick={() => regenerate(s as Size)}
            className={`px-4 py-1 rounded-lg ${size === s
              ? "bg-orange-500 text-white"
              : "bg-orange-100 text-orange-700 hover:bg-orange-200"
              }`}
          >
            {s}x{s}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div
        className="grid gap-1 sm:gap-2 mx-auto"
        style={{
          gridTemplateColumns: `repeat(${size}, 1fr)`,
          width: size === 4 ? "220px" : "320px",
          maxWidth: "95vw",
        }}
      >
        {grid.map((row, r) =>
          row.map((cell, c) => {
            const locked =
              solution[r][c] === grid[r][c] &&
              cell !== 0;

            return (
              <input
                key={`${r}-${c}`}
                value={cell || ""}
                onChange={(e) =>
                  handleChange(e.target.value, r, c)
                }
                maxLength={1}
                disabled={locked}
                className={`
                  aspect-square w-full text-center rounded-md
                  text-xs sm:text-sm md:text-base
                  transition-all
                  ${isIncorrect(r, c)
                    ? "border-2 border-red-500 bg-red-50"
                    : "border border-orange-200 focus:border-orange-500"
                  }
                  ${locked
                    ? "bg-orange-100 font-semibold text-orange-700"
                    : "bg-white"
                  }
                `}
              />
            );
          })
        )}
      </div>

      <button
        onClick={() => regenerate(size)}
        className="mt-6 px-5 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition"
      >
        Restart
      </button>

      {completed && (
        <p className="mt-4 text-green-600 font-medium">
          Completed
        </p>
      )}
    </div>
  );
};

export default FocusGrid;